import React, { useCallback } from 'react'
import { Link } from 'gatsby'
import { PER_PAGE } from '@/utils/const'
import { StaticImage } from 'gatsby-plugin-image'
import { fireEvent } from '@/utils/activityLog'
import * as style from './index.module.css'

interface Props {
  /** ニュースの総数 */
  totalCount: number
  /** 現在のページ */
  currentPage: number
}

/** @file ページネーション */
export const Pagination = ({ totalCount, currentPage }: Props) => {
  /** 総ページ数 */
  const totalPages: number = Math.ceil(totalCount / PER_PAGE)

  const getPageNumbers = () => {
    /** ページ数が5以下の場合は全て表示 */
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    /** 現在のページが3以下の場合は1,2,3,4,...,最終ページを表示 */
    if (currentPage <= 3) {
      return [1, 2, 3, 4, '...', totalPages]
    }

    /** 現在のページが最終ページから3以下の場合は1,...,最終ページ-3,最終ページ-2,最終ページ-1,最終ページを表示 */
    if (currentPage >= totalPages - 2) {
      return [
        1,
        '...',
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ]
    }

    /** それ以外の場合は1,...,現在のページ-1,現在のページ,現在のページ+1,...,最終ページを表示 */
    return [
      1,
      '...',
      currentPage - 1,
      currentPage,
      currentPage + 1,
      '...',
      totalPages,
    ]
  }

  const pageNumbers = getPageNumbers()

  const onClickNextPagination = useCallback((): void => {
    fireEvent({
      event: 'clickNextPagination',
      parameters: {
        current_index: currentPage.toString(),
        next_index: (currentPage + 1).toString(),
      },
    })
  }, [currentPage])

  const onClickBackPagination = useCallback((): void => {
    fireEvent({
      event: 'clickBackPagination',
      parameters: {
        current_index: currentPage.toString(),
        next_index: (currentPage - 1).toString(),
      },
    })
  }, [currentPage])

  const onClickIndexPagination = useCallback(
    (nextIndex: number): void => {
      fireEvent({
        event: 'clickIndexPagination',
        parameters: {
          current_index: currentPage.toString(),
          next_index: nextIndex.toString(),
        },
      })
    },
    [currentPage],
  )

  return (
    <div className={style.root}>
      <PaginationArrow
        isDisabled={currentPage <= 1}
        to={`/news/all/${currentPage - 1}`}
        onClick={onClickBackPagination}
        type="back"
      />
      <div className={style.pageNumbers}>
        {pageNumbers.map((number, index) => (
          <span key={index}>
            {typeof number === 'number' ? (
              <Link
                to={`/news/all/${number}`}
                className={
                  number === currentPage
                    ? style.selectNumberText
                    : style.numberText
                }
                onClick={() => onClickIndexPagination(number)}
              >
                {number}
              </Link>
            ) : (
              <span className={style.columnText}>{number}</span>
            )}
          </span>
        ))}
      </div>
      <PaginationArrow
        isDisabled={currentPage >= totalPages}
        to={`/news/all/${currentPage + 1}`}
        onClick={onClickNextPagination}
        type="next"
      />
    </div>
  )
}

/** ページネーションの矢印コンポーネント */
const PaginationArrow = ({
  isDisabled,
  to,
  onClick,
  type,
}: {
  isDisabled: boolean
  to: string
  onClick: () => void
  type: 'back' | 'next'
}) => (
  <div className={style.arrow}>
    {type === 'back' ? (
      <StaticImage
        src="../../../images/icon/arrowleft-color-black.svg"
        loading="eager"
        formats={['auto', 'webp', 'avif']}
        alt="ロゴ"
        className={style.arrowLogo}
      />
    ) : null}
    {isDisabled ? (
      <span className={`${style.arrowText} ${style.disabled}`}>
        {type === 'next' ? '次のページ' : '前のページ'}
      </span>
    ) : (
      <Link to={to} className={style.arrowText} onClick={onClick}>
        {type === 'next' ? '次のページ' : '前のページ'}
      </Link>
    )}
    {type === 'next' ? (
      <StaticImage
        src="../../../images/icon/arrowrigh-color-black.svg"
        loading="eager"
        formats={['auto', 'webp', 'avif']}
        alt="ロゴ"
        className={style.arrowLogo}
      />
    ) : null}
  </div>
)
